import { useContext, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import type { BusinessSubscription, LensSDKException } from "lens-platform-sdk";
import { Button, ButtonBar } from "@k8slens/lds";

import { useLogout } from "src/hooks/useLogout";
import { ProfileContext } from "src/providers/profile-provider";
import { useGetBusinessSubscriptions } from "src/hooks/useGetBusinessSubscriptions";
import { useCreateBusinessInvitation } from "src/hooks/useCreateBusinessInvitation";
import { useGetBusiness } from "src/hooks/useGetBusiness";
import { useGetBusinessInvitations } from "src/hooks/useGetBusinessInvitations";
import { useAnalytics } from "src/hooks/useAnalytics";
import { getUniqueEmailsArray } from "src/services/email";

import Layout from "src/components/Layout/Layout";
import AssigneeTable from "src/components/Business/AssigneeTable";
import AssignSubscriptionModal from "src/components/Business/AssignSubscriptionModal";
import Notification from "src/components/Notification/Notification";
import { useLensPlatformClient } from "src/hooks/useLensPlatformClient";

import { useGetBusinessUsers } from "src/hooks/useGetBusinessUsers";
import { PendingSubscriptionInvitation } from "src/services/getInvitationState";
import { ActivationLinkInvitation } from "src/hooks/useGetActivationLinkInvitation";
import { useDeleteBusinessInvitation } from "src/hooks/useDeleteBusinessInvitation";
import styles from "./ManageSeats.module.css";

const assignSeatsLabel = "Assign Seats";

/**
 * The "Manage Seats" page for the business, rendered at /business/:businessId/subscriptions
 */
const ManageSeats = () => {
  const { track, trackError, trackButtonClicked } = useAnalytics();
  const logout = useLogout();
  const { profile } = useContext(ProfileContext);

  const { businessId } = useParams<{ businessId?: string }>();
  const {
    businessSubscriptions,
    loading: loadingBusinessSubscriptions,
    reload: reloadBusinessSubscriptions
  } = useGetBusinessSubscriptions(businessId);
  const {
    businessInvitations,
    loading: loadingBusinessInvitations,
    reload: reloadBusinessInvitations
  } = useGetBusinessInvitations(businessId);
  const { businessUsers } = useGetBusinessUsers(businessId);
  const { business, reload: reloadBusiness } = useGetBusiness(businessId);
  const lensPlatformClient = useLensPlatformClient();
  const { createBusinessInvitation, loading: creatingBusinessInvitation } = useCreateBusinessInvitation(businessId);
  const [createBusinessInvitationError, setCreateBusinessInvitationError] = useState<LensSDKException>();
  const [seatsTableActiveTabIndex, setSeatsTableActiveTabIndex] = useState<number>(0);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const { deleteBusinessInvitation, loading: deleteBusinessInvitationLoading } = useDeleteBusinessInvitation();

  const [assignSubscriptionModalOpen, setAssignSubscriptionModalOpen] = useState(false);
  const openAssignSubscriptionModal = useCallback(() => {
    trackButtonClicked(assignSeatsLabel);
    setAssignSubscriptionModalOpen(true);
  }, [trackButtonClicked]);
  const closeAssignSubscriptionModal = useCallback(() => {
    setAssignSubscriptionModalOpen(false);
    setCreateBusinessInvitationError(undefined);
  }, []);

  const onClickAssign = useCallback(async (emails: string, selectedSubscriptions: BusinessSubscription) => {
    const emailArray = getUniqueEmailsArray(emails);
    const subscriptionId = selectedSubscriptions.id;

    setCreateBusinessInvitationError(undefined);

    for (const email of emailArray) {
      // eslint-disable-next-line no-await-in-loop
      const exception = await createBusinessInvitation(email, subscriptionId);

      if (exception) {
        setCreateBusinessInvitationError(exception);
        trackError("Assigning Seats to Subscription Failed", { subscriptionId: selectedSubscriptions });

        return exception;
      }
      track("Assigned Seats to Subscription", { subscriptionId: selectedSubscriptions });
    }
    await Promise.all([
      reloadBusinessSubscriptions(),
      reloadBusinessInvitations()
    ]);
    closeAssignSubscriptionModal();

    // Set active tab to "Pending" to show assigned seat.
    if (seatsTableActiveTabIndex !== 1) {
      setSeatsTableActiveTabIndex(1);
    }

    return null;
  }, [
    createBusinessInvitation,
    closeAssignSubscriptionModal,
    reloadBusinessSubscriptions,
    reloadBusinessInvitations,
    seatsTableActiveTabIndex,
    track, trackError
  ]);

  const handleRemoveBusinessInvitation = async (invitation: PendingSubscriptionInvitation |
  ActivationLinkInvitation) => {
    if (!businessId || !invitation) {
      return;
    }

    await deleteBusinessInvitation(businessId, invitation.id, "link" in invitation);

    await reloadBusiness(businessId);
    await reloadBusinessInvitations();
  };

  const handleUnassignSeat = async (seatId: string) => {
    setError(null);
    setLoading(true);

    if (!businessId || !seatId) {
      return;
    }

    try {
      await lensPlatformClient.business.deActivateBusinessUserSubscription({
        businessId,
        businessSubscriptionId: seatId,
        username: profile?.username!
      });

      track("Seat unassigned", { seatId });

      await reloadBusinessSubscriptions();
      await reloadBusinessInvitations();
    } catch (error) {
      if (error instanceof Error) {
        setError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Layout className="bg-secondary" logout={logout} profile={profile} business={business}>
        <div>
          <div>
            {
              error && <Notification type="flash" level="error" message={error.message} />
            }
            <AssigneeTable
              businessSubscriptions={businessSubscriptions}
              businessInvitations={businessInvitations}
              businessUsers={businessUsers}
              selectedTableTabIndex={seatsTableActiveTabIndex}
              setSelectedTableTabIndex={setSeatsTableActiveTabIndex}
              onRemoveBusinessInvitation={handleRemoveBusinessInvitation}
              onUnassignSeat={handleUnassignSeat}
              loading={loading || deleteBusinessInvitationLoading}
              business={business}
            />
            <ButtonBar type="grid" className={styles.buttonBar}>
              <Button
                label={assignSeatsLabel}
                onClick={() => {
                  openAssignSubscriptionModal();
                }}
                primary
                disabled={businessSubscriptions.length === 0}
              />
            </ButtonBar>
          </div>
        </div>
      </Layout>
      <AssignSubscriptionModal
        open={assignSubscriptionModalOpen}
        onModalClose={closeAssignSubscriptionModal}
        onClickAssign={onClickAssign}
        businessSubscriptions={businessSubscriptions}
        businessInvitations={businessInvitations}
        loadingBusinessSubscriptions={loadingBusinessSubscriptions}
        loadingBusinessInvitations={loadingBusinessInvitations}
        assigningSubscription={creatingBusinessInvitation}
        assignSubscriptionError={createBusinessInvitationError}
        disabled={creatingBusinessInvitation}
      />
    </>
  );
};

export default ManageSeats;
